import React, { useState, useEffect, createContext } from "react";

import { useNavigate } from "react-router-dom";

import { api, createSession, getUsuarioLogado } from "../services/api";

export const AuthContext = createContext();

/**
 * Decide a rota de destino pós-login com base no perfil do usuário.
 *
 * Estratégia:
 * 1. Tipos legados (1, 2, 3) mantêm o comportamento histórico para não quebrar
 *    bookmarks/hábitos existentes.
 * 2. Se o usuário tem `permissoes` configuradas, vai para a PRIMEIRA permissão
 *    listada (é a rota "principal" do perfil dele).
 * 3. Fallback final: "/" — o AppRoutes HomeRedirect decide a partir dali (trata
 *    tipo 4 = tickets, ex: suporte).
 */
const resolveHomeRoute = (user) => {
  if (!user) return "/";

  // Comportamento histórico — preservado para admin/produtos/vendas
  if (user.tipo === 1) return "/";
  if (user.tipo === 2) return "/produtos";
  if (user.tipo === 3) return "/vendas";

  // Usa permissoes configuradas quando disponíveis
  const perms = Array.isArray(user.permissoes) ? user.permissoes : [];
  if (perms.length > 0) {
    // Primeira permissão costuma ser a rota principal do perfil
    return perms[0];
  }

  // Fallback: "/" → HomeRedirect no AppRoutes roteia corretamente
  return "/";
};

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  /**
   * Acerta o usuário do localStorage com o servidor, em segundo plano.
   *
   * O localStorage é gravado no login e nunca mais era revisitado, então quem
   * entrou antes de uma mudança de permissão ficava com a lista velha para
   * sempre e batia em 404 numa tela que já deveria acessar (ticket #2299, num
   * iPhone cuja sessão era mais antiga que a do Android do mesmo usuário).
   *
   * Falha de rede NÃO desloga e NÃO limpa nada: instabilidade não pode tirar
   * ninguém do sistema. Só o 401 (token morto) derruba a sessão, e isso o
   * interceptor da api já trata.
   */
  const revalidarUsuario = async (usuarioEmCache) => {
    const atual = await getUsuarioLogado();
    if (!atual) return;

    if (!atual.permissoes) atual.permissoes = [];

    // Sem mudança: não mexe no estado, para não provocar re-render à toa.
    if (JSON.stringify(atual) === JSON.stringify(usuarioEmCache)) return;

    localStorage.setItem("user", JSON.stringify(atual));
    setUser(atual);
  };

  useEffect(() => {
    const recoverUser = window.localStorage.getItem("user");

    if (recoverUser === 'undefined' || !recoverUser) {
      setLoading(false);
      return;
    }

    const parsed = JSON.parse(recoverUser);
    // Garante que permissoes existe (compatibilidade com sessoes antigas)
    if (!parsed.permissoes) parsed.permissoes = [];
    setUser(parsed);

    // O `loading` só cai DEPOIS da revalidação, de propósito.
    //
    // Enquanto ele é true, o Private mostra "Carregando..." e não decide rota.
    // Sem isso vira corrida: o Private avalia com a lista velha, manda para
    // /not_found, e quando a lista nova chega ninguém traz o usuário de volta.
    // Passou nos testes no Safari e falhou no Chrome só por causa da ordem em
    // que as respostas chegaram.
    //
    // A espera é curta e limitada: getUsuarioLogado tem timeout de 3s e nunca
    // rejeita, então a tela sempre libera, com a lista nova ou com a do cache.
    revalidarUsuario(parsed).finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    const response = await createSession(email, password);
    if (response == false) {
      setUser(null);
      return false;
    }
    // Usuario bloqueado pelo admin: devolve a mensagem para a tela de login.
    if (response.erro) {
      setUser(null);
      return response;
    }
    const loggerUser = response.data.data.user;
    const token = response.data.data.token;

    if (!loggerUser.permissoes) loggerUser.permissoes = [];

    api.defaults.headers.Authorization = `Bearer ${token}`;

    localStorage.setItem("user", JSON.stringify(loggerUser));
    localStorage.setItem("token", token);

    setUser(loggerUser);
    navigate(resolveHomeRoute(loggerUser));
    return true;
  };

  const loginWithToken = (token, userData) => {
    if (!userData.permissoes) userData.permissoes = [];

    api.defaults.headers.Authorization = `Bearer ${token}`;
    localStorage.setItem("user", JSON.stringify(userData));
    localStorage.setItem("token", token);
    setUser(userData);
    navigate(resolveHomeRoute(userData));
    return true;
  };

  const logout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("token");

    api.defaults.headers.Authorization = null;

    setUser(null);
    navigate("/login");
    window.location.reload();
  };
  return (
    <AuthContext.Provider
      value={{ authenticated: !!user, user, loading, login, loginWithToken, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
};
